import { useState, type ChangeEvent, type FormEvent } from "react";
import {
  ExchangeRateClientError,
  getExchangeRates,
} from "./api/exchange-rates";
import type { ExchangeRateSnapshot } from "../../../shared/contracts/exchange-rates";
import type { CountryResult, QueryResponse } from "../../../shared/contracts/query";
import { QueryClientError, queryCustoms } from "./api/query-client";
import { AppShell } from "../../components/AppShell";
import {
  convertValue,
  estimateTariffBreakdown,
  isCalculableHsCode,
  isValidHsCode,
  normalizeHsCode,
  parseCurrency,
  parseDeclaredValue,
  parseExcelBuffer,
  parseQuantity,
  type CalcRow,
  type Currency,
  type DestinationCountry,
  type ExchangeRates,
  type ParsedExcelRow,
  type TariffBreakdown,
} from "./tariff-calc";

function localToday(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

type CalcResultRow = {
  readonly key: string;
  readonly name: string;
  readonly hsCode: string;
  readonly quantity: number | null;
  readonly declaredValue: string;
  readonly currency: Currency;
  readonly convertedValue: string | null;
  readonly estimate: TariffBreakdown;
};

const STATUS_LABEL: Record<TariffBreakdown["status"], string> = {
  confirmed: "可估算",
  manual_review: "人工复核",
  not_found: "未找到",
  needs_rate: "需汇率",
};

function toSafeError(error: unknown): string {
  if (error instanceof QueryClientError) return error.message;
  return "计算暂时不可用，请稍后重试";
}

function toExchangeRateError(error: unknown): string {
  if (error instanceof ExchangeRateClientError) return error.message;
  return "官方汇率暂时不可用";
}

function snapshotIsUsable(snapshot: ExchangeRateSnapshot | null): boolean {
  if (!snapshot || snapshot.status !== "live") return false;
  const expiryTimes = [
    snapshot.rates.USD.expiresAt,
    snapshot.rates.CAD.expiresAt,
    snapshot.rates.CNY.expiresAt,
  ].map((value) => Date.parse(value));
  return expiryTimes.every((value) => Number.isFinite(value) && value > Date.now());
}

function exchangeStatusLabel(snapshot: ExchangeRateSnapshot): string {
  return snapshot.status === "live" ? "实时" : "缓存";
}

function exchangeSelectionLabel(snapshot: ExchangeRateSnapshot): string {
  switch (snapshot.selection) {
    case "same_day":
      return "当天";
    case "latest_business_day":
      return "最近工作日";
    case "cached":
      return "缓存";
  }
}

function exchangeSources(snapshot: ExchangeRateSnapshot): string {
  return [...new Set([
    snapshot.rates.USD.source,
    snapshot.rates.CAD.source,
    snapshot.rates.CNY.source,
  ])].join(" / ");
}

type ResolvedExchangeRates = {
  readonly rates: ExchangeRates;
  readonly snapshot: ExchangeRateSnapshot | null;
};

async function queryCalculatorCode(
  code: string,
  ruleDate: string,
  destination: DestinationCountry,
): Promise<QueryResponse> {
  const baseRequest = {
    query: code,
    ruleDate,
    attributes: { originCountry: "CN" },
  } as const;
  const initial = await queryCustoms(baseRequest);
  if (initial.results.some((item) => item.country === destination)) return initial;

  const hs6 = code.slice(0, 6);
  if (!initial.candidates.some((candidate) => candidate.hs6 === hs6)) return initial;

  return queryCustoms({
    ...baseRequest,
    codeCountry: destination,
    selectedHs6: hs6,
    querySessionId: initial.queryId,
  });
}

function urlPrefill(): { name: string; hsCode: string; destination: DestinationCountry } {
  const params = new URLSearchParams(window.location.search);
  const country = params.get("country");
  return {
    name: params.get("name")?.trim() ?? "",
    hsCode: params.get("hs")?.trim() ?? "",
    destination: country === "CA" ? "CA" : country === "US" ? "US" : "US",
  };
}

export function TariffCalculatorPage({ defaultRuleDate = localToday() }: { defaultRuleDate?: string }) {
  const [urlPrefilled] = useState(urlPrefill);
  const [ruleDate, setRuleDate] = useState(defaultRuleDate);
  const [destination, setDestination] = useState<DestinationCountry>(urlPrefilled.destination);
  const [exchangeSnapshot, setExchangeSnapshot] = useState<ExchangeRateSnapshot | null>(null);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [exchangeRateError, setExchangeRateError] = useState<string | null>(null);

  const [name, setName] = useState(urlPrefilled.name);
  const [hsCode, setHsCode] = useState(urlPrefilled.hsCode);
  const [quantity, setQuantity] = useState("");
  const [declaredValue, setDeclaredValue] = useState("");
  const [currency, setCurrency] = useState<Currency>("USD");
  const [formIssues, setFormIssues] = useState<string[]>([]);

  const [importRows, setImportRows] = useState<readonly ParsedExcelRow[] | null>(null);
  const [importFileName, setImportFileName] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<readonly CalcResultRow[] | null>(null);
  const [dataNote, setDataNote] = useState<string | null>(null);

  const targetCurrency: Currency = destination === "US" ? "USD" : "CAD";
  const validImportCount = importRows?.filter((item) => item.row !== null).length ?? 0;

  async function refreshExchangeRates(asOfDate: string): Promise<ExchangeRateSnapshot | null> {
    setExchangeRateLoading(true);
    setExchangeRateError(null);
    try {
      const snapshot = await getExchangeRates(undefined, asOfDate);
      setExchangeSnapshot(snapshot);
      return snapshot;
    } catch (reason) {
      setExchangeRateError(toExchangeRateError(reason));
      setExchangeSnapshot((previous) => previous ? { ...previous, status: "cached" } : previous);
      return null;
    } finally {
      setExchangeRateLoading(false);
    }
  }

  async function resolveExchangeRates(rows: readonly CalcRow[]): Promise<ResolvedExchangeRates> {
    const cachedRates = exchangeSnapshot?.calculatorRates;
    const needsAutomaticRate = rows.some((row) => {
      if (row.currency === targetCurrency) return false;
      if (row.currency === "CNY") {
        return destination === "US" ? !cachedRates?.usdCny : !cachedRates?.cadCny;
      }
      return !cachedRates?.usdCny || !cachedRates?.cadCny;
    });
    if (!needsAutomaticRate || snapshotIsUsable(exchangeSnapshot)) {
      return { rates: cachedRates ?? {}, snapshot: exchangeSnapshot };
    }

    const freshSnapshot = await refreshExchangeRates(ruleDate);
    if (!freshSnapshot) return { rates: {}, snapshot: null };
    return { rates: freshSnapshot.calculatorRates, snapshot: freshSnapshot };
  }

  async function runCalculation(rows: readonly CalcRow[]) {
    const uniqueCodes = [...new Set(rows.map((row) => row.hsCode))];
    if (uniqueCodes.length > 50) {
      setError("单次最多计算 50 个不同 HS 编码，请分批导入");
      return;
    }
    const shortCodes = uniqueCodes.filter((code) => !isCalculableHsCode(code));
    if (shortCodes.length > 0) {
      setError(`关税计算器需要 6-10 位 HS CODE 才能查询费率；以下编码需先完成归类或人工复核：${shortCodes.join("、")}`);
      return;
    }
    setPending(true);
    setError(null);
    setResults(null);
    setDataNote(null);
    try {
      const resolvedExchange = await resolveExchangeRates(rows);
      const rates = resolvedExchange.rates;
      const resultByCode = new Map<string, CountryResult | null>();
      const responseByCode = new Map<string, QueryResponse>();
      for (const code of uniqueCodes) {
        const response = await queryCalculatorCode(code, ruleDate, destination);
        responseByCode.set(code, response);
        resultByCode.set(code, response.results.find((item) => item.country === destination) ?? null);
      }
      setResults(rows.map((row, index) => {
        const converted = convertValue(row, destination, rates);
        const result = resultByCode.get(row.hsCode) ?? null;
        const response = responseByCode.get(row.hsCode);
        const estimate = estimateTariffBreakdown(result, converted, destination, {
          ready: response?.dataStatus.ready === true,
          testData: response?.testData !== false,
        });
        return {
          key: `${index}-${row.hsCode}`,
          name: row.name,
          hsCode: row.hsCode,
          quantity: row.quantity,
          currency: row.currency,
          declaredValue: `${row.declaredValue} ${row.currency}`,
          convertedValue: converted.value ? `${converted.value.toFixed(2)} ${converted.currency}` : null,
          estimate,
        };
      }));
      const notes: string[] = [];
      const responseNotes = [...responseByCode.values()].map((response) => {
        if (response.testData) return `关税数据状态：测试版（评估时间 ${response.dataStatus.evaluatedAt}），结果仅供演示，不能用于报关或税费结论。`;
        if (!response.dataStatus.ready) return `关税数据状态：未达到正式发布门禁（评估时间 ${response.dataStatus.evaluatedAt}），结果仅供参考。`;
        return `关税数据状态：已达到正式发布门禁（评估时间 ${response.dataStatus.evaluatedAt}）。`;
      });
      notes.push(...responseNotes.filter((note, index, all) => all.indexOf(note) === index));
      if (resolvedExchange.snapshot) notes.push(`汇率：${exchangeStatusLabel(resolvedExchange.snapshot)}，来源 ${exchangeSources(resolvedExchange.snapshot)}，汇率日期 ${resolvedExchange.snapshot.effectiveDate}（${exchangeSelectionLabel(resolvedExchange.snapshot)}），更新时间 ${resolvedExchange.snapshot.fetchedAt}`);
      if (exchangeRateError) notes.push(`${exchangeRateError}，未自动填入估算金额。`);
      if (notes.length > 0) setDataNote(notes.join(" "));
    } catch (reason) {
      setError(toSafeError(reason));
    } finally {
      setPending(false);
    }
  }

  function handleSingleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const issues: string[] = [];
    const trimmedName = name.trim();
    const normalizedCode = normalizeHsCode(hsCode);
    const parsedQuantity = parseQuantity(quantity);
    const parsedDeclaredValue = parseDeclaredValue(declaredValue);
    const parsedCurrency = parseCurrency(currency);
    if (!trimmedName) issues.push("请填写品名");
    if (!isValidHsCode(normalizedCode)) issues.push("HS CODE 应为 4-10 位数字");
    else if (!isCalculableHsCode(normalizedCode)) issues.push("关税计算器需要 6-10 位 HS CODE；4-5 位编码请先完成归类或人工复核");
    if (quantity.trim() && parsedQuantity === null) issues.push("单件数量应为大于 0 的整数");
    if (parsedDeclaredValue === null) issues.push("申报货值应为大于等于 0 的数字");
    if (!parsedCurrency) issues.push("币种应为 USD / CAD / CNY");
    setFormIssues(issues);
    if (issues.length > 0 || parsedDeclaredValue === null || parsedCurrency === null) return;
    void runCalculation([{
      name: trimmedName,
      hsCode: normalizedCode,
      quantity: parsedQuantity,
      declaredValue: parsedDeclaredValue,
      currency: parsedCurrency,
    }]);
  }

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportError(null);
    setResults(null);
    try {
      const parsed = await parseExcelBuffer(await file.arrayBuffer());
      setImportRows(parsed.rows);
      if (!parsed.headerFound) {
        setImportError("未识别到模板表头（品名 / HS CODE / 单件数量 / 申报货值 / 币种），请使用官网模板");
      }
    } catch {
      setImportRows([]);
      setImportError("Excel 读取失败，请确认文件未损坏");
    }
  }

  function handleBatchCalculate() {
    const validRows = (importRows ?? []).flatMap((item) => item.row ? [item.row] : []);
    if (validRows.length === 0) {
      setError("没有可计算的行，请先导入有效模板数据");
      return;
    }
    void runCalculation(validRows);
  }

  return (
    <AppShell
      active="calculator"
      mainId="calculator-workspace"
      title="Customs Clearance · Duties · Delivery"
      subtitle="中国出口视角 · 美国 / 加拿大进口税费估算"
      status={<span className="calculator-status">{dataNote ? "已生成计算结果" : "运行计算后显示数据状态"}</span>}
    >
      <div className="calculator-main">
        <section className="query-intro">
          <p className="eyebrow">税费估算工作台</p>
          <h2>单项计算或 Excel 批量导入</h2>
      <p>从 HS 查询结果可直接带入品名和编码；从价税填写货值即可。数量仅用于识别从量/复合税风险，当前不会自动换算，仍需人工复核。</p>
        </section>

        <div className="calculator-grid">
          <section className="calculator-column" aria-label="计算条件">
            <form className="search-panel" onSubmit={handleSingleSubmit}>
              <div className="search-panel-heading">
                <div>
                  <p className="eyebrow">单项计算</p>
                  <h3>填写商品信息</h3>
                </div>
              </div>
              {urlPrefilled.name || urlPrefilled.hsCode ? <p className="support-note">已从 HS 查询带入品名与编码，填写货值即可。数量仅用于识别从量/复合税风险，当前不会自动换算，仍需人工复核。</p> : null}
              <div className="calculator-fields">
                <div className="search-field">
                  <label htmlFor="calc-name">品名</label>
                  <input id="calc-name" value={name} onChange={(event) => setName(event.target.value)} maxLength={120} placeholder="例如：乳胶床垫" />
                </div>
                <div className="search-field">
                  <label htmlFor="calc-hs">HS CODE</label>
                  <input id="calc-hs" value={hsCode} onChange={(event) => setHsCode(event.target.value)} maxLength={20} placeholder="例如：9404.21.00" />
                </div>
                <div className="search-field">
                  <label htmlFor="calc-qty">单件数量</label>
                  <small className="field-hint">仅用于识别从量/复合税风险，不自动换算</small>
                  <input id="calc-qty" inputMode="numeric" value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder="可填写数量，例如：120（仅作风险提示）" />
                </div>
                <div className="search-field">
                  <label htmlFor="calc-value">申报货值</label>
                  <input id="calc-value" inputMode="decimal" value={declaredValue} onChange={(event) => setDeclaredValue(event.target.value)} placeholder="例如：10200" />
                </div>
                <div className="search-field">
                  <label htmlFor="calc-currency">币种</label>
                  <select id="calc-currency" value={currency} onChange={(event) => setCurrency(event.target.value as Currency)}>
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                    <option value="CNY">CNY</option>
                  </select>
                </div>
              </div>
              <div className="calc-options">
                <label>
                  目的国
                  <select value={destination} onChange={(event) => setDestination(event.target.value as DestinationCountry)}>
                    <option value="US">美国</option>
                    <option value="CA">加拿大</option>
                  </select>
                </label>
                <label>
                  规则日期
                  <input type="date" value={ruleDate} onChange={(event) => setRuleDate(event.target.value)} required />
                </label>
              </div>
              <section className="exchange-rate-panel" aria-label="自动汇率状态">
                <div className="exchange-rate-heading">
                  <div>
                    <strong>自动汇率</strong>
                    <span>
                      按规则日期读取 CBSA 公共汇率；不可用时自动回退加拿大央行（BoC）最近工作日汇率
                      {destination === "US" ? "；美国结果为交叉换算参考，不替代 CBP 申报汇率" : ""}
                    </span>
                  </div>
                  <button
                    className="secondary-button exchange-rate-refresh"
                    type="button"
                    onClick={() => { void refreshExchangeRates(ruleDate); }}
                    disabled={exchangeRateLoading || pending}
                  >
                    {exchangeRateLoading ? "读取中…" : "刷新汇率"}
                  </button>
                </div>
                {exchangeSnapshot ? (
                  <p className="exchange-rate-status" aria-live="polite">
                    <span className={`exchange-rate-badge ${exchangeSnapshot.status}`}>{exchangeStatusLabel(exchangeSnapshot)}</span>
                    来源：{exchangeSources(exchangeSnapshot)} · 汇率日期：{exchangeSnapshot.effectiveDate}（{exchangeSelectionLabel(exchangeSnapshot)}）· 获取时间：{exchangeSnapshot.fetchedAt}
                    <br />
                    自动换算：1 USD = {exchangeSnapshot.calculatorRates.usdCny} CNY；1 CAD = {exchangeSnapshot.calculatorRates.cadCny} CNY
                    <br />
                    有效期：{exchangeSnapshot.rates.CAD.effectiveAt} 至 {exchangeSnapshot.rates.CAD.expiresAt}
                  </p>
                ) : (
                  <p className="exchange-rate-status">尚未读取；使用 CNY 或跨币种货值计算时会按规则日期自动获取。</p>
                )}
                {exchangeRateError ? <p className="exchange-rate-error" role="alert">{exchangeRateError}；未自动填入估算金额，请稍后重试。</p> : null}
              </section>
              {formIssues.length > 0 ? (
                <ul className="form-issues" role="alert">
                  {formIssues.map((issue) => <li key={issue}>{issue}</li>)}
                </ul>
              ) : null}
              <button className="primary-button calculator-submit" type="submit" disabled={pending}>
                {pending ? "计算中…" : "开始计算"}
              </button>
            </form>

            <section className="import-panel" aria-label="Excel 批量导入">
              <div className="search-panel-heading">
                <div>
                  <p className="eyebrow">批量导入</p>
                  <h3>上传 Excel 模板</h3>
                </div>
              </div>
              <p className="support-note">
                下载 <a href="/关税计算器_导入模板.xlsx" download>导入模板</a>，按“品名 / HS CODE / 单件数量（仅用于风险提示） / 申报货值 / 币种”填写后上传；从量/复合税仍需人工复核。
              </p>
              <input className="file-input" type="file" accept=".xlsx,.xls" onChange={handleFile} />
              {importFileName ? <p className="muted-text">已选择：{importFileName}</p> : null}
              {importError ? <p className="import-error" role="alert">{importError}</p> : null}
              {importRows ? (
                <div className="import-preview">
                  <h4>导入预览（{validImportCount}/{importRows.length} 行有效）</h4>
                  <div className="table-scroll">
                    <table className="calc-table">
                      <thead>
                        <tr><th>行</th><th>品名</th><th>HS CODE</th><th>数量</th><th>申报货值</th><th>币种</th><th>校验</th></tr>
                      </thead>
                      <tbody>
                        {importRows.map((item) => (
                          <tr key={item.line}>
                            <td>{item.line}</td>
                            <td>{item.row?.name ?? "—"}</td>
                            <td>{item.row?.hsCode ?? "—"}</td>
                            <td>{item.row?.quantity ?? "未填写"}</td>
                            <td>{item.row?.declaredValue ?? "—"}</td>
                            <td>{item.row?.currency ?? "—"}</td>
                            <td>{item.issues.length > 0 ? <span className="calc-status not_found">{item.issues.join("；")}</span> : "有效"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <button className="primary-button calculator-submit" type="button" onClick={handleBatchCalculate} disabled={pending}>
                    {pending ? "计算中…" : "开始批量计算"}
                  </button>
                </div>
              ) : null}
            </section>
          </section>

          <section className="result-column" aria-label="计算结果区域">
            {pending ? <p className="loading-line" aria-live="polite">正在计算…</p> : null}
            {error ? (
              <section className="error-panel" role="alert">
                <h2>计算未完成</h2>
                <p>{error}</p>
              </section>
            ) : null}
            {!pending && !error && !results ? (
              <section className="coverage-panel calculator-empty" aria-label="计算说明">
                <div className="coverage-item"><strong>1</strong><span>单项计算</span><small>填一行商品信息，直接出估算结果</small></div>
                <div className="coverage-item"><strong>N</strong><span>Excel 批量</span><small>上传模板后逐行计算并汇总展示</small></div>
                <div className="coverage-item"><strong>!</strong><span>未确认税率</span><small>只显示人工复核状态，不生成金额</small></div>
              </section>
            ) : null}
            {results ? (
              <section className="result-panel" aria-labelledby="calc-results-heading">
                <div className="section-heading">
                  <div>
                    <p className="eyebrow">计算结果</p>
                    <h2 id="calc-results-heading">估算结果</h2>
                  </div>
                  <span className="muted-text">目的国：{destination === "US" ? "美国" : "加拿大"} · {ruleDate}</span>
                </div>
                {dataNote ? <p className="calc-data-note">{dataNote}</p> : null}
                <div className="table-scroll">
                  <table className="calc-table">
                    <thead>
                      <tr><th>品名</th><th>HS CODE</th><th>数量</th><th>申报货值</th><th>税率</th><th>估算关税</th><th>状态 / 说明</th></tr>
                    </thead>
                    <tbody>
                      {results.map((row) => (
                        <tr key={row.key}>
                          <td>{row.name}</td>
                          <td>{row.hsCode}</td>
                          <td>{row.quantity ?? "未填写"}</td>
                          <td>{row.declaredValue}{row.convertedValue ? <small className="calc-converted">（{row.convertedValue}）</small> : null}</td>
                          <td>{row.estimate.ratePercent === null ? "—" : `${row.estimate.ratePercent}%`}</td>
                          <td>{row.estimate.estimatedAmount === null ? "—" : `${row.estimate.estimatedAmount} ${targetCurrency}`}</td>
                          <td>
                            <span className={`calc-status ${row.estimate.status}`}>{STATUS_LABEL[row.estimate.status]}</span>
                            {row.estimate.note ? <small className="calc-note">{row.estimate.note}</small> : null}
                            <details className="calc-tax-details">
                              <summary>税费明细</summary>
                              {row.estimate.lines.length === 0 ? <p className="calc-tax-empty">无已确认税费行</p> : null}
                              <div className="calc-tax-lines">
                                {row.estimate.lines.map((line) => (
                                  <div key={line.id} className={`calc-tax-line ${line.status}`}>
                                    <strong>{line.label}</strong>
                                    <span>{line.ratePercent === null ? line.rateExpressionRaw : `${line.ratePercent}%`}</span>
                                    <span>基数 {line.base}</span>
                                    <span>{line.amount === null ? "—" : `${line.amount} ${targetCurrency}`}</span>
                                    <small>{line.status === "included" ? `来源生效日期 ${line.effectiveFrom}` : line.note}</small>
                                  </div>
                                ))}
                              </div>
                              {row.estimate.valueForDuty ? <p className="calc-tax-total">货值（VFD）：{row.estimate.valueForDuty} {targetCurrency}</p> : null}
                              {row.estimate.valueForTax ? <p className="calc-tax-total">加拿大计税基础（value for tax）：{row.estimate.valueForTax} {targetCurrency}</p> : null}
                              {row.estimate.customsPayable ? <p className="calc-tax-total">预计应缴：{row.estimate.customsPayable} {targetCurrency}</p> : null}
                            </details>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="disclosure">估算仅包含已确认的官方税费行（基础关税、附加税、贸易救济、消费税、联邦进口 GST、官方费用）；不含省级税、代理费、仓储费和承运人费用；数量不会自动参与从量/复合税换算；未确认项目不生成金额，核对后才能用于报关。</p>
              </section>
            ) : null}
          </section>
        </div>
      </div>
    </AppShell>
  );
}
